export class SessionRefreshUnavailableError extends Error {
  readonly status = 503;
  constructor() {
    super('Unable to reconnect your session. Please try again.');
    this.name = 'SessionRefreshUnavailableError';
  }
}

interface RefreshSessionOptions {
  apiUrl: string;
  syncCsrf: (response: Response) => void;
  headers: () => Record<string, string>;
}

/** False means the server confirmed expiry; connection failures never end a session. */
export async function refreshSession(options: RefreshSessionOptions): Promise<boolean> {
  const probe = async (signal: AbortSignal): Promise<'signed-in' | 'expired' | 'no-session'> => {
    const response = await fetch(`${options.apiUrl}/auth/me`, {
      credentials: 'include', cache: 'no-store', headers: options.headers(), signal,
    });
    options.syncCsrf(response);
    if (response.ok) return 'signed-in';
    if (response.status !== 401) throw new SessionRefreshUnavailableError();
    // A browser with no manager refresh cookie (a resident or owner session) has nothing to renew.
    const body = await response.json().catch(() => null) as { refreshable?: boolean } | null;
    return body?.refreshable === false ? 'no-session' : 'expired';
  };
  const renew = async (signal: AbortSignal): Promise<boolean> => {
    // A sibling tab may have refreshed while we were waiting for the lock.
    const before = await probe(signal);
    if (before !== 'expired') return before === 'signed-in';
    const response = await fetch(`${options.apiUrl}/auth/refresh`, {
      method: 'POST', credentials: 'include', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }, body: '{}', signal,
    });
    options.syncCsrf(response);
    if (response.ok) {
      const data = await response.json() as { success?: boolean };
      if (data.success === true) return true;
      throw new SessionRefreshUnavailableError();
    }
    if (response.status === 400) return false; // No refresh cookie.
    if (response.status === 401 || response.status === 409) {
      // Also recover races with old tabs or browsers without Web Locks. The
      // winning response's cookies may arrive shortly after the losing one.
      for (const delay of [0, 100, 300, 600, 1000, 2000]) {
        if (delay) await new Promise<void>((resolve, reject) => {
          signal.throwIfAborted();
          const onAbort = () => { clearTimeout(timer); reject(signal.reason); };
          const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
          }, delay);
          signal.addEventListener('abort', onAbort, { once: true });
        });
        if (await probe(signal) === 'signed-in') return true;
      }
      return false;
    }
    throw new SessionRefreshUnavailableError();
  };
  const refresh = async (): Promise<boolean> => {
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), 10_000);
    try {
      return await renew(controller.signal);
    } finally {
      clearTimeout(deadline);
    }
  };
  try {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return await navigator.locks.request('lionshare-session-refresh', refresh);
    }
    return await refresh();
  } catch {
    throw new SessionRefreshUnavailableError();
  }
}
